"use client"

import dynamic from "next/dynamic";
import { NewsCardObject } from "@/lib/types";
import { ImageCard } from "./ImageCard";
import { TextCard } from "./TextCard";
import { LinkCard } from "./LinkCard";
import { getVideoPosterUrl } from "@/lib/news_helpers";

// The heavy renderers load on demand instead of landing in the shared
// client chunk. Statically imported, VideoCard's module-scope hls.js
// (the heaviest dep in package.json) and CardCarousel's embla shipped
// to every page that imports NewsFeed — video, city, district, and tag
// — even when the rendered card is a text or image post (Aug 2026
// Lighthouse audit: ~80KB+ unused JS parsed per page from this chunk).
// These still SSR normally (no ssr:false), so the served HTML is
// unchanged; only the hydration chunk becomes lazy. ImageCard,
// TextCard, and LinkCard stay static — they're tiny, and a dynamic()
// round trip would cost more than it saves.
const VideoCard = dynamic(() =>
  import("./VideoCard").then((m) => m.VideoCard),
);
const CardCarousel = dynamic(() =>
  import("./CardCarousel").then((m) => m.CardCarousel),
);
const YouTubeCard = dynamic(() =>
  import("./YoutubeCard").then((m) => m.YouTubeCard),
);

// Only the fields the renderers below read. VideoCard (a Server Component)
// builds this from the full card so the ~3KB object (summary, share text,
// location metadata…) isn't serialized into the RSC payload for hydration.
export type CardBodyCard = Pick<
  NewsCardObject,
  'id' | 'type' | 'post_sub_type' | 'hw_ratio' | 'media_list' | 'summary_text' | 'title'
> & {
  data: Pick<
    NewsCardObject['data'],
    'thumbnail_url' | 'share_image_url' | 'video_url' | 'image_url' | 'source_url' | 'youtube_url'
  >;
};

export function CardBody({card}: {card: CardBodyCard}){
    if(card.type === 'UGC_VIDEO'){
        const posterUrl = getVideoPosterUrl(card.data);
        return <VideoCard id={card.id} poster={posterUrl} url={card.data.video_url} hwRatio={card.hw_ratio} />
    }
    if(card.type === 'UGC_IMAGE'){
        return <ImageCard id={card.id} url={card.data.image_url} alt={card.summary_text || card.title || 'Image post'} />
    }
    if(card.type === 'UGC_TEXT'){
        return <TextCard text={card.summary_text} />
    }
    if(card.type === 'UGC_LINK'){
        return <LinkCard url={card.data.source_url} />
    }
    if(card.type === 'YOUTUBE_VIDEO'){
        return <YouTubeCard id={card.id} url={card.data.youtube_url || ''} />
    }
    if((card.type === 'CAROUSEL' || card.post_sub_type === 'CAROUSEL' || card.type === 'WEB_MEDIA') && Array.isArray(card.media_list)){
        return <CardCarousel mediaList={card.media_list} />
    }

    return (
        <div className="relative w-full bg-gray-200 aspect-[4/3]">
            <div className='flex items-center justify-center w-full h-full bg-gray-300 text-gray-500'>
                No post available
            </div>
        </div>
    )
}